<template>
  <div class="wechat-login-panel">
    <div class="login-header">
      <h3 class="login-title">{{ $t('auth.wechat.title') }}</h3>
      <p class="login-subtitle">{{ $t('auth.wechat.subtitle') }}</p>
    </div>

    <div class="qr-section">
      <div class="qr-container">
        <div id="wechat-qr" class="qr-code"></div>
        <div v-if="qrState !== 'ready'" class="qr-overlay">
          <template v-if="qrState === 'agreement'">
            <div class="qr-frame">
              <el-icon class="placeholder-icon" :size="48">
                <Icon name="material-symbols:shield-lock-outline" />
              </el-icon>
            </div>
            <p class="placeholder-text">{{ $t('auth.wechat.agreementRequired') }}</p>
          </template>
          <template v-else-if="qrState === 'loading'">
            <div class="qr-frame">
              <el-icon class="loading-icon" :size="48">
                <Icon name="material-symbols:hourglass-empty" />
              </el-icon>
            </div>
            <p class="loading-text">{{ $t('auth.wechat.generating') }}</p>
          </template>
          <template v-else-if="qrState === 'processing'">
            <div class="qr-frame">
              <el-icon class="loading-icon" :size="48">
                <Icon name="material-symbols:cached" />
              </el-icon>
            </div>
            <p class="loading-text">{{ $t('auth.wechat.processing') }}</p>
          </template>
          <template v-else>
            <el-button
              type="primary"
              @click="refreshQr"
              size="large"
            >
              {{ $t('auth.wechat.refreshQr') }}
            </el-button>
            <p class="expired-text">{{ errorMessage || $t('auth.wechat.qrExpired') }}</p>
          </template>
        </div>
      </div>
    </div>

    <div class="login-tips">
      <p class="tips-text">{{ $t('auth.wechat.tips') }}</p>
    </div>

    <div class="agreement-section">
      <AgreementCheckbox
        v-model="agreementsAccepted"
        @update:model-value="handleAgreementChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { LoginData } from '~/types/user'
import { WECHAT_LOGIN_SUCCESS_KEY, useWeChatAuth } from '~/composables/useWeChatAuth'

interface Props {
  agreementsAccepted: boolean
}

interface Emits {
  (e: 'agreements-change', value: boolean): void
  (e: 'login-success'): void
}

type WeChatLoginSuccessPayload = LoginData & { timestamp?: number }

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const userStore = useUserStore()
const { t } = useI18n()
const { $event } = useNuxtApp()
const { initWeChatQR, stateKey } = useWeChatAuth()

const QR_CONTAINER_ID = 'wechat-qr'

const resolveQrStyleHref = () => {
  if (import.meta.server || typeof window === 'undefined') {
    return ''
  }
  try {
    return new URL('/wechat-login-lite.css', window.location.origin).toString()
  } catch {
    return ''
  }
}

const agreementsAccepted = ref(props.agreementsAccepted)
const loading = ref(false)
const iframeProcessing = ref(false)
const errorMessage = ref('')
const hasRendered = ref(false)
const showRefresh = computed(() => Boolean(errorMessage.value))
const qrState = computed<'agreement' | 'loading' | 'processing' | 'error' | 'ready'>(() => {
  if (!agreementsAccepted.value) return 'agreement'
  if (loading.value) return 'loading'
  if (iframeProcessing.value) return 'processing'
  if (showRefresh.value) return 'error'
  return 'ready'
})

watch(() => props.agreementsAccepted, (newValue) => {
  agreementsAccepted.value = newValue
  if (!newValue) {
    resetQr()
  }
})

watch(
  () => agreementsAccepted.value,
  (value) => {
    if (value) {
      initQr()
    } else {
      resetQr()
    }
  }
)

onMounted(() => {
  $event.on('auth-modal-closed', handleModalClosed)
  if (import.meta.client) {
    window.addEventListener('storage', handleStorageEvent)
    window.addEventListener('message', handleMessageEvent)
  }
  nextTick(() => {
    if (agreementsAccepted.value) {
      initQr()
    }
  })
})

onUnmounted(() => {
  $event.off('auth-modal-closed', handleModalClosed)
  if (import.meta.client) {
    window.removeEventListener('storage', handleStorageEvent)
    window.removeEventListener('message', handleMessageEvent)
  }
})

const handleMessageEvent = (event: MessageEvent) => {
  if (import.meta.server) {
    return
  }
  const origin = window.location.origin
  if (event.origin !== origin) {
    return
  }
  const data = event.data as { type?: string; status?: string; message?: string } | null
  if (!data || data.type !== 'wechat-login-status') {
    return
  }
  if (data.status === 'processing') {
    iframeProcessing.value = true
    errorMessage.value = ''
  } else if (data.status === 'success') {
    iframeProcessing.value = false
  } else if (data.status === 'error') {
    iframeProcessing.value = false
    errorMessage.value = data.message || t('common.actionFailed')
  }
}

const initQr = async (force = false) => {
  if (import.meta.server) {
    return
  }
  if (!agreementsAccepted.value || loading.value) {
    return
  }
  if (hasRendered.value && !force) {
    return
  }

  loading.value = true
  errorMessage.value = ''
  try {
    await nextTick()
    await initWeChatQR(QR_CONTAINER_ID, {
      selfRedirect: true,
      styleLite: true,
      styleHref: resolveQrStyleHref()
    })
    hasRendered.value = true
  } catch (error) {
    hasRendered.value = false
    errorMessage.value = error instanceof Error ? error.message : t('common.actionFailed')
    console.error('[WeChatLoginPanel] initWeChatQR failed:', error)
  } finally {
    loading.value = false
  }
}

const refreshQr = () => {
  if (!agreementsAccepted.value) {
    errorMessage.value = t('auth.wechat.agreementRequired')
    return
  }
  initQr(true)
}

const resetQr = () => {
  hasRendered.value = false
  errorMessage.value = ''
  loading.value = false
  iframeProcessing.value = false
  if (import.meta.client) {
    localStorage.removeItem(stateKey.value)
    const container = document.getElementById(QR_CONTAINER_ID)
    if (container) {
      container.innerHTML = ''
    }
  }
}

const handleStorageEvent = async (event: StorageEvent) => {
  if (event.key !== WECHAT_LOGIN_SUCCESS_KEY || !event.newValue) {
    return
  }
  try {
    const payload = JSON.parse(event.newValue) as WeChatLoginSuccessPayload
    await userStore.setLoginData({
      uid: payload.uid,
      token: payload.token,
      refreshToken: payload.refreshToken,
      userName: payload.userName,
      avatarUrl: payload.avatarUrl
    })
    if (import.meta.client) {
      localStorage.removeItem(WECHAT_LOGIN_SUCCESS_KEY)
      localStorage.removeItem(stateKey.value)
    }
    emit('login-success')
    resetQr()
  } catch (error) {
    console.error('[WeChatLoginPanel] handleStorageEvent failed:', error)
  }
}

const cleanupSuccessSignal = () => {
  if (import.meta.client) {
    localStorage.removeItem(WECHAT_LOGIN_SUCCESS_KEY)
  }
}

const handleAgreementChange = (value: boolean) => {
  emit('agreements-change', value)
}

const handleModalClosed = () => {
  resetQr()
  cleanupSuccessSignal()
}
</script>

<style scoped lang="scss">
.wechat-login-panel {
  --qr-width: 260px;
  --qr-height: 260px;
  padding: 32px 32px 24px;
  min-height: 480px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  .login-header {
    text-align: center;
    margin-bottom: 32px;
    width: 100%;

    .login-title {
      font-size: 24px;
      font-weight: 600;
      color: #1f2937;
      margin: 0 0 8px 0;
    }

    .login-subtitle {
      font-size: 14px;
      color: #6b7280;
      margin: 0;
      line-height: 1.5;
    }
  }

  .agreement-section {
    margin-top: auto;
    padding-top: 24px;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .qr-section {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: var(--qr-height);
    margin-bottom: 16px;
    width: 100%;

    .qr-container {
      position: relative;
      width: 100%;
      max-width: var(--qr-width);
      min-height: var(--qr-height);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .qr-code {
      width: var(--qr-width);
      height: var(--qr-height);
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;

      :deep(iframe) {
        border: none;
        display: block;
        width: var(--qr-width) !important;
        height: var(--qr-height) !important;
      }
    }

    .qr-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.92);
      backdrop-filter: blur(2px);
      border-radius: 8px;
      padding: 16px;
      text-align: center;
    }

    .qr-frame {
      width: calc(var(--qr-width) - 20px);
      height: calc(var(--qr-height) - 20px);
      border: 2px solid #e5e7eb;
      border-radius: 8px;
      background: #f9fafb;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 12px;
      background-image:
        linear-gradient(to right, #4A90E2 3px, transparent 3px),
        linear-gradient(to bottom, #4A90E2 3px, transparent 3px),
        linear-gradient(to left, #4A90E2 3px, transparent 3px),
        linear-gradient(to bottom, #4A90E2 3px, transparent 3px),
        linear-gradient(to right, #4A90E2 3px, transparent 3px),
        linear-gradient(to top, #4A90E2 3px, transparent 3px),
        linear-gradient(to left, #4A90E2 3px, transparent 3px),
        linear-gradient(to top, #4A90E2 3px, transparent 3px);
      background-size:
        24px 3px, 3px 24px,
        24px 3px, 3px 24px,
        24px 3px, 3px 24px,
        24px 3px, 3px 24px;
      background-position:
        8px 8px, 8px 8px,
        calc(100% - 8px) 8px, calc(100% - 8px) 8px,
        8px calc(100% - 8px), 8px calc(100% - 8px),
        calc(100% - 8px) calc(100% - 8px), calc(100% - 8px) calc(100% - 8px);
      background-repeat: no-repeat;
    }

    .placeholder-text,
    .loading-text,
    .expired-text {
      font-size: 14px;
      color: #6b7280;
      text-align: center;
      line-height: 1.6;
      margin: 8px 0 0 0;
    }

    .refresh-icon,
    .loading-icon {
      color: #9ca3af;
      animation: spin 2s linear infinite;
    }
  }

  .login-tips {
    text-align: center;
    margin-bottom: 16px;
    width: 100%;
    
    .tips-text {
      font-size: 13px;
      color: #6b7280;
      margin: 0;
      line-height: 1.5;
      font-weight: 400;
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes frame-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.98);
  }
}

// 响应式设计
@media (max-width: 768px) {
  .wechat-login-panel {
    --qr-width: 220px;
    --qr-height: 220px;
    padding: 24px 20px;
    min-height: auto;
    gap: 12px;

    .login-header {
      margin-bottom: 24px;
      
      .login-title {
        font-size: 20px;
      }

      .login-subtitle {
        font-size: 13px;
      }
    }

    .agreement-section {
      padding-top: 16px;
    }
  }
}
</style>
